/* vc-home-compare.js — Colour Theory Guide before/after slider.

   Auto-reveal, once at least `data-threshold` of the frame is on screen:
     50 -> 100%  0.7s   (show all Before)
     hold        0.4s
     100 -> 0%   1.4s   (show all After)
     hold        0.4s
     0 -> 50%    0.7s
   about 3.6s in all, repeating every `data-interval` ms while it stays in
   view, stopping when it leaves and starting fresh on return.

   Any drag, tap or key on the range input cancels the sweep at once; it
   resumes only after the same interval with no interaction. Off entirely
   under prefers-reduced-motion, where the slider stays draggable.

   Loaded with defer; depends on vc-home-motion.js for the shared rAF loop. */
(function () {
  'use strict';

  function setup(root) {
    if (root.__vcCompare) return;
    var range = root.querySelector('[data-vch-compare-range]');
    var frame = root.querySelector('[data-vch-compare-frame]') || root;
    if (!range) return;
    root.__vcCompare = true;

    var VC = window.VCHome || {};
    var reduce = !!VC.reduce;
    var ease = VC.easeInOutQuad || function (x) { return x; };

    var auto = root.getAttribute('data-auto') !== '0';
    var interval = parseFloat(root.getAttribute('data-interval'));
    if (isNaN(interval)) interval = 4000;
    var threshold = parseFloat(root.getAttribute('data-threshold'));
    if (isNaN(threshold)) threshold = 0.35;
    threshold = Math.min(Math.max(threshold, 0), 1);

    var pos = parseFloat(range.value);
    if (isNaN(pos)) pos = 50;

    var state = {
      inView: false,
      anim: null,
      nextAt: 0,
      lastUser: -1e9
    };

    function paint(p) {
      pos = p;
      frame.style.setProperty('--vch-pos', p + '%');
      /* Keep the input in step so keyboard and AT read the right value. */
      if (Math.abs(parseFloat(range.value) - p) > 0.05) range.value = String(p);
      range.setAttribute('aria-valuenow', String(Math.round(p)));
    }

    paint(pos);

    /* ---- user interaction wins ------------------------------------------ */
    function userTouched() {
      var now = performance.now();
      state.anim = null;
      state.lastUser = now;
      state.nextAt = now + interval;
    }

    range.addEventListener('input', function () {
      userTouched();
      var v = parseFloat(range.value);
      if (!isNaN(v)) paint(v);
    });
    range.addEventListener('pointerdown', userTouched);
    range.addEventListener('keydown', userTouched);

    if (reduce || !auto) return; /* draggable, but never self-animating */

    /* ---- visibility ----------------------------------------------------- */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (en) {
            var seen = en.isIntersecting && en.intersectionRatio >= threshold;
            if (seen === state.inView) return;
            state.inView = seen;
            if (!seen) {
              /* Leave it as the user last saw it and start fresh on return. */
              state.anim = null;
              state.nextAt = 0;
            }
          });
        },
        { threshold: [0, threshold, Math.min(threshold + 0.25, 1)] }
      );
      io.observe(frame);
      root.__vcCompareObs = io;
    } else {
      state.inView = true;
    }

    /* ---- the sweep ------------------------------------------------------ */
    function step(t) {
      if (!state.inView) return;

      if (!state.anim) {
        if (t >= state.nextAt && t - state.lastUser > interval) {
          state.anim = { t0: t, from: pos };
        } else {
          return;
        }
      }

      var e = (t - state.anim.t0) / 1000;
      var from = state.anim.from;
      var p;

      if (e < 0.7) p = from + (100 - from) * ease(e / 0.7);
      else if (e < 1.1) p = 100;
      else if (e < 2.5) p = 100 - 100 * ease((e - 1.1) / 1.4);
      else if (e < 2.9) p = 0;
      else if (e < 3.6) p = 50 * ease((e - 2.9) / 0.7);
      else {
        p = 50;
        state.anim = null;
        state.nextAt = t + interval;
      }

      if (Math.abs(p - pos) > 0.15 || !state.anim) paint(p);
    }

    if (VC.addJob) root.__vcStop = VC.addJob(step);
  }

  function initAll(r) {
    (r || document).querySelectorAll('[data-vch-compare]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-vch-compare]').forEach(function (el) {
      if (el.__vcStop) el.__vcStop();
      if (el.__vcCompareObs) el.__vcCompareObs.disconnect();
    });
  });
})();
